import axios from 'axios';

/**
 * Build an axios CancelToken from an AbortController signal, so a debounced
 * type-ahead can abort the previous in-flight request (SHER-352).
 *
 * WHY THIS LIVES HERE / USES THE BASE `axios`:
 * SHER-352 originally created the token with `axios.CancelToken.source()` inside
 * `services/index.js`, where `axios` is the shared *instance* (`utils/axios`,
 * built via `axios.create()`). An axios instance does NOT expose `.CancelToken`
 * — only the base module does — so `axios.CancelToken` was `undefined` and every
 * drug/chemical-component search threw
 * `TypeError: Cannot read properties of undefined (reading 'source')` *before*
 * issuing the request. The AutocompleteSherwood catch then set `errorSearch`,
 * showed "No results", and no network call was ever made — the prescription drug
 * search was fully broken. Using the base `axios` here fixes it; the returned
 * token is still compatible with the instance's `.get({ cancelToken })`.
 *
 * Fail-soft: if `CancelToken` is ever unavailable (e.g. a future axios bump that
 * drops it), return `undefined` instead of throwing, so a missing cancel token
 * can never again silently kill the whole search.
 */
export function cancelTokenFromSignal(signal?: AbortSignal) {
    if (!signal) return undefined;
    const CancelToken = (axios as any).CancelToken;
    if (!CancelToken || typeof CancelToken.source !== 'function') {
        return undefined;
    }
    const source = CancelToken.source();
    if (signal.aborted) {
        source.cancel();
    } else {
        signal.addEventListener('abort', () => source.cancel(), { once: true });
    }
    return source.token;
}
